import { useEffect, useRef, useState } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Calculator, DollarSign, TrendingDown, Info, Download, Mail, Building2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

interface CompetitorData {
  name: string;
  rate: number;
  monthlyFee: number;
  color: string;
}

const defaultCompetitors: CompetitorData[] = [
  { name: "Square", rate: 2.6, monthlyFee: 0, color: "bg-secondary" },
  { name: "PayPal", rate: 2.99, monthlyFee: 0, color: "bg-muted-foreground" },
  { name: "Stripe", rate: 2.9, monthlyFee: 0, color: "bg-primary" },
  { name: "Clover", rate: 2.3, monthlyFee: 14.95, color: "bg-accent" },
  { name: "Toast", rate: 2.49, monthlyFee: 0, color: "bg-destructive" },
  { name: "Heartland", rate: 2.4, monthlyFee: 25, color: "bg-muted" },
  { name: "WorldPay", rate: 2.75, monthlyFee: 19.95, color: "bg-secondary" },
  { name: "Processing Bros", rate: 1.99, monthlyFee: 0, color: "bg-accent" },
];

const EnhancedFeeCalculator = () => {
  const [monthlyVolume, setMonthlyVolume] = useState('');
  const [totalMonthlyFee, setTotalMonthlyFee] = useState('');
  const [avgTicket, setAvgTicket] = useState('');
  const [businessType, setBusinessType] = useState('');
  const [currentProcessor, setCurrentProcessor] = useState('');
  const [customRate, setCustomRate] = useState('');
  const [email, setEmail] = useState('');
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [results, setResults] = useState<{
    percentage: number;
    monthlyFees: number;
    annualFees: number;
    potentialSavings: number;
    competitorComparison: { name: string; monthlyCost: number; annualCost: number; savings: number; color: string }[];
    projections: { month: string; currentFees: number; withUs: number; savings: number }[];
  } | null>(null);
  const { toast } = useToast();
  const resultsRef = useRef<HTMLDivElement>(null);

  const calculateFees = () => {
    const volume = parseFloat(monthlyVolume);
    const fee = parseFloat(totalMonthlyFee);

    if (!volume || !fee || volume <= 0 || fee <= 0) {
      toast({
        title: "Invalid Input",
        description: "Please enter valid numbers for both fields",
        variant: "destructive",
      });
      return;
    }

    const percentage = (fee / volume) * 100;
    const monthlyFees = fee;
    const annualFees = monthlyFees * 12;
    
    // Build competitors list - add custom processor if provided
    let competitors = [...defaultCompetitors];
    if (currentProcessor && customRate) {
      const customRateNum = parseFloat(customRate);
      if (customRateNum > 0) {
        // Remove any existing entry with same name and add custom
        competitors = competitors.filter(c => c.name.toLowerCase() !== currentProcessor.toLowerCase());
        competitors.unshift({ 
          name: currentProcessor, 
          rate: customRateNum, 
          monthlyFee: 0, 
          color: "bg-destructive" 
        });
      }
    }
    
    // Calculate competitor comparison
    const competitorComparison = competitors.map(comp => {
      const monthlyCost = (volume * comp.rate / 100) + comp.monthlyFee;
      const annualCost = monthlyCost * 12;
      return {
        name: comp.name,
        monthlyCost,
        annualCost,
        savings: annualFees - annualCost,
        color: comp.color,
      };
    }).sort((a, b) => a.monthlyCost - b.monthlyCost);

    // Calculate 12-month projections
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const currentMonth = new Date().getMonth();
    const ourRate = 0.0199; // 1.99%
    
    const projections = months.map((month, index) => {
      const monthIndex = (currentMonth + index) % 12;
      const seasonalMultiplier = [1.0, 0.9, 0.95, 1.0, 1.05, 1.1, 1.15, 1.1, 1.0, 1.05, 1.2, 1.3][monthIndex];
      const adjustedVolume = volume * seasonalMultiplier;
      const currentFees = adjustedVolume * (percentage / 100);
      const withUs = adjustedVolume * ourRate;
      
      return {
        month: months[monthIndex],
        currentFees: Math.round(currentFees),
        withUs: Math.round(withUs),
        savings: Math.round(currentFees - withUs),
      };
    });

    const targetRate = 1.99;
    const targetAnnualFees = (volume * 12) * (targetRate / 100);
    const potentialSavings = Math.max(0, annualFees - targetAnnualFees);

    setResults({
      percentage,
      monthlyFees,
      annualFees,
      potentialSavings,
      competitorComparison,
      projections,
    });
    
    toast({
      title: "Analysis Complete",
      description: "Your fee comparison is ready!",
    });
  };

  const generatePDFContent = () => {
    if (!results) return '';
    
    const pdfData = {
      generatedAt: new Date().toLocaleDateString(),
      businessType: businessType || 'Not specified',
      monthlyVolume: parseFloat(monthlyVolume),
      currentRate: results.percentage.toFixed(2),
      currentMonthlyFees: results.monthlyFees,
      currentAnnualFees: results.annualFees,
      potentialSavings: results.potentialSavings,
      competitorComparison: results.competitorComparison,
      projections: results.projections,
    };
    
    return JSON.stringify(pdfData, null, 2);
  };

  const handleDownloadQuote = () => {
    if (!results) return;

    const content = `
PROCESSING BROS - PERSONALIZED RATE ANALYSIS
============================================
Generated: ${new Date().toLocaleDateString()}
${businessType ? `Business Type: ${businessType}` : ''}

YOUR CURRENT SITUATION
----------------------
Monthly Volume: $${parseFloat(monthlyVolume).toLocaleString()}
Current Processing Rate: ${results.percentage.toFixed(2)}%
Monthly Fees: $${results.monthlyFees.toLocaleString()}
Annual Fees: $${results.annualFees.toLocaleString()}

COMPETITOR COMPARISON
---------------------
${results.competitorComparison.map(c => 
  `${c.name}: $${c.monthlyCost.toFixed(0)}/mo | Annual Savings: $${c.savings > 0 ? c.savings.toFixed(0) : '0'}`
).join('\n')}

12-MONTH PROJECTIONS WITH PROCESSING BROS
------------------------------------------
${results.projections.map(p => 
  `${p.month}: Current $${p.currentFees} → With Us $${p.withUs} (Save $${p.savings})`
).join('\n')}

TOTAL POTENTIAL ANNUAL SAVINGS: $${results.potentialSavings.toLocaleString()}

Ready to save? Contact us at:
Phone: (954) 399-2230
Email: support@processingbros.com
Web: https://processingbros.com/free-rate-analysis
    `;

    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ProcessingBros-Quote-${new Date().toISOString().split('T')[0]}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    toast({
      title: "Quote Downloaded",
      description: "Your personalized quote has been saved.",
    });
  };

  const handleEmailResults = async () => {
    if (!email || !results) return;

    toast({
      title: "Results Sent",
      description: `Your analysis has been emailed to ${email}`,
    });
    setShowEmailDialog(false);
    setEmail('');
  };

  return (
    <TooltipProvider>
      <Card className="w-full max-w-4xl mx-auto">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl font-bold flex items-center justify-center gap-2">
            <Calculator className="h-6 w-6" />
            Advanced Fee Calculator & ROI Analyzer
          </CardTitle>
          <CardDescription>
            Compare your rates against major competitors and see your potential savings over time.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <Label htmlFor="monthlyVolume">Monthly Volume ($)</Label>
              <Input
                id="monthlyVolume"
                type="number"
                value={monthlyVolume}
                onChange={(e) => setMonthlyVolume(e.target.value)}
                placeholder="e.g., 50000"
                min="0"
                step="0.01"
              />
            </div>
            <div>
              <Label htmlFor="totalMonthlyFee">Total Monthly Fees ($)</Label>
              <Input
                id="totalMonthlyFee"
                type="number"
                value={totalMonthlyFee}
                onChange={(e) => setTotalMonthlyFee(e.target.value)}
                placeholder="e.g., 1450"
                min="0"
                step="0.01"
              />
            </div>
            <div>
              <Label htmlFor="avgTicket">Avg. Transaction ($)</Label>
              <Input
                id="avgTicket"
                type="number"
                value={avgTicket}
                onChange={(e) => setAvgTicket(e.target.value)}
                placeholder="e.g., 45"
                min="0"
                step="0.01"
              />
            </div>
            <div>
              <Label htmlFor="businessType">Business Type</Label>
              <Select value={businessType} onValueChange={setBusinessType}>
                <SelectTrigger>
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="restaurant">Restaurant</SelectItem>
                  <SelectItem value="retail">Retail</SelectItem>
                  <SelectItem value="bar-nightclub">Bar / Nightclub</SelectItem>
                  <SelectItem value="convenience">Convenience Store</SelectItem>
                  <SelectItem value="professional">Professional Services</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="currentProcessor">Current Processor (Optional)</Label>
              <Input
                id="currentProcessor"
                type="text"
                value={currentProcessor}
                onChange={(e) => setCurrentProcessor(e.target.value)}
                placeholder="e.g., First Data"
              />
            </div>
            <div>
              <Label htmlFor="customRate">Their Rate % (Optional)</Label>
              <Input
                id="customRate"
                type="number"
                value={customRate}
                onChange={(e) => setCustomRate(e.target.value)}
                placeholder="e.g., 2.75"
                min="0"
                max="10"
                step="0.01"
              />
            </div>
          </div>

          <Button 
            onClick={calculateFees}
            variant="premium"
            size="lg"
            className="w-full"
          >
            <Calculator className="h-5 w-5 mr-2" />
            Analyze My Processing Fees
          </Button>

            {results && (
            <div ref={resultsRef} className="mt-8 space-y-8 animate-fade-in">
              {/* Current Stats */}
              <div className="p-6 rounded-lg border border-border bg-gradient-to-br from-muted/40 via-background to-muted/20">
                <h3 className="text-lg font-semibold mb-4 text-center">Your Current Processing Fees</h3>
                <div className="grid md:grid-cols-3 gap-4 text-center">
                  <div className="bg-card p-4 rounded-lg shadow-sm border border-border">
                    <DollarSign className="h-8 w-8 mx-auto mb-2 text-primary" />
                    <div className="text-2xl font-bold">
                      ${results.monthlyFees.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                    </div>
                    <div className="text-sm text-muted-foreground">Monthly Fees</div>
                  </div>
                  <div className="bg-card p-4 rounded-lg shadow-sm border border-border">
                    <div className="text-3xl font-bold text-destructive">
                      {results.percentage.toFixed(2)}%
                    </div>
                    <div className="text-sm text-muted-foreground">Effective Rate</div>
                  </div>
                  <div className="bg-card p-4 rounded-lg shadow-sm border border-border">
                    <TrendingDown className="h-8 w-8 mx-auto mb-2 text-accent" />
                    <div className="text-2xl font-bold text-accent">
                      ${results.potentialSavings.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                    </div>
                    <div className="text-sm text-muted-foreground">Potential Annual Savings</div>
                  </div>
                </div>
              </div>

              {/* Competitor Comparison */}
              <div className="p-6 bg-card rounded-lg border border-border">
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <Building2 className="h-5 w-5" />
                  Competitor Comparison
                </h3>
                <div className="space-y-3">
                  {results.competitorComparison.map((comp, index) => (
                    <div 
                      key={comp.name}
                      className={`flex items-center justify-between p-3 rounded-lg border ${
                        comp.name === 'Processing Bros' 
                          ? 'bg-accent/10 border-accent' 
                          : 'bg-muted/50 border-border'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <span className={`w-3 h-3 rounded-full ${comp.color}`} />
                        <span className={`font-medium ${comp.name === 'Processing Bros' ? 'text-accent' : ''}`}>
                          {comp.name}
                          {index === 0 && <span className="ml-2 text-xs bg-accent text-accent-foreground px-2 py-0.5 rounded">Lowest</span>}
                        </span>
                      </div>
                      <div className="text-right">
                        <div className="font-semibold">${comp.monthlyCost.toLocaleString(undefined, { maximumFractionDigits: 0 })}/mo</div>
                        {comp.savings > 0 && (
                          <div className="text-xs text-accent">Save ${comp.savings.toLocaleString(undefined, { maximumFractionDigits: 0 })}/yr</div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* 12-Month Projections */}
              <div className="p-6 bg-card rounded-lg border border-border">
                <h3 className="text-lg font-semibold mb-4">12-Month Savings Projection</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="text-left py-2 px-2">Month</th>
                        <th className="text-right py-2 px-2">Current Fees</th>
                        <th className="text-right py-2 px-2">With Us</th>
                        <th className="text-right py-2 px-2 text-accent">Monthly Savings</th>
                      </tr>
                    </thead>
                    <tbody>
                      {results.projections.map((proj) => (
                        <tr key={proj.month} className="border-b border-border/50">
                          <td className="py-2 px-2 font-medium">{proj.month}</td>
                          <td className="text-right py-2 px-2">${proj.currentFees.toLocaleString()}</td>
                          <td className="text-right py-2 px-2">${proj.withUs.toLocaleString()}</td>
                          <td className="text-right py-2 px-2 text-accent font-semibold">${proj.savings.toLocaleString()}</td>
                        </tr>
                      ))}
                      <tr className="bg-accent/10 font-bold">
                        <td className="py-2 px-2">Total</td>
                        <td className="text-right py-2 px-2">
                          ${results.projections.reduce((sum, p) => sum + p.currentFees, 0).toLocaleString()}
                        </td>
                        <td className="text-right py-2 px-2">
                          ${results.projections.reduce((sum, p) => sum + p.withUs, 0).toLocaleString()}
                        </td>
                        <td className="text-right py-2 px-2 text-accent">
                          ${results.projections.reduce((sum, p) => sum + p.savings, 0).toLocaleString()}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-4">
                <Button onClick={handleDownloadQuote} variant="outline" className="flex-1">
                  <Download className="h-4 w-4 mr-2" />
                  Download Quote
                </Button>
                <Dialog open={showEmailDialog} onOpenChange={setShowEmailDialog}>
                  <DialogTrigger asChild>
                    <Button variant="outline" className="flex-1">
                      <Mail className="h-4 w-4 mr-2" />
                      Email Results
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Email Your Results</DialogTitle>
                      <DialogDescription>
                        Enter your email address and we will send you a copy of your fee analysis.
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 pt-4">
                      <Input
                        type="email"
                        placeholder="your@email.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                      />
                      <Button onClick={handleEmailResults} className="w-full">
                        Send Results
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
                <Button variant="premium" className="flex-1" asChild>
                  <a href="/free-rate-analysis">Get Your Free Rate Analysis</a>
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </TooltipProvider>
  );
};

export default EnhancedFeeCalculator;
